import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Flag, X } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const REASONS = [
    { id: "inappropriate", label: "Inappropriate or offensive content" },
    { id: "scam",          label: "Scam or fraud" },
    { id: "misleading",    label: "Misleading listing" },
    { id: "harassment",    label: "Harassment or abusive behaviour" },
    { id: "illegal",       label: "Illegal content" },
    { id: "other",         label: "Other" },
];

/**
 * Report dialog reused for rooms, users and messages.
 * Props: { targetType: "room" | "user" | "message", targetId, targetLabel, open, onOpenChange }
 */
export default function ReportDialog({ targetType, targetId, targetLabel, open, onOpenChange }) {
    const [reason, setReason] = useState("inappropriate");
    const [description, setDescription] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const submit = async () => {
        setSubmitting(true);
        try {
            await api.post("/reports", { target_type: targetType, target_id: targetId, reason, description });
            toast.success("Thanks — we've received your report and will review it within 24 hours.");
            onOpenChange(false);
            setDescription("");
            setReason("inappropriate");
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't submit report");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    key="backdrop"
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="fixed inset-0 z-[75] bg-foreground/40 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
                    onClick={() => onOpenChange(false)}
                    data-testid="report-dialog-backdrop"
                    style={{ paddingBottom: "max(1rem, env(safe-area-inset-bottom))" }}
                >
                    <motion.div
                        initial={{ opacity: 0, y: 40, scale: 0.96 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 20, scale: 0.98 }}
                        transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                        onClick={(e) => e.stopPropagation()}
                        data-testid="report-dialog"
                        className="w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden"
                    >
                        <div className="relative p-5 sm:p-6 text-white"
                            style={{ background: "linear-gradient(135deg, #f4a261 0%, #ee8f6d 45%, #dd6b4d 100%)" }}>
                            <button onClick={() => onOpenChange(false)} aria-label="Close"
                                className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors"
                                data-testid="report-dialog-close">
                                <X className="w-4 h-4" />
                            </button>
                            <div className="flex items-center gap-3">
                                <div className="w-12 h-12 rounded-2xl bg-white/95 text-primary flex items-center justify-center shadow-lg shrink-0">
                                    <Flag className="w-6 h-6" strokeWidth={2.2} />
                                </div>
                                <div className="min-w-0">
                                    <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-white/80">Report</div>
                                    <h2 className="font-display text-xl sm:text-2xl font-bold leading-tight truncate">
                                        {targetLabel || "Report this content"}
                                    </h2>
                                </div>
                            </div>
                        </div>
                        <div className="p-5 sm:p-6 space-y-4">
                            <p className="text-sm text-muted-foreground">
                                Choose the reason that best describes the problem. Reports are reviewed by our moderators within 24 hours.
                            </p>
                            <div className="space-y-2" role="radiogroup" aria-label="Reason for report">
                                {REASONS.map((r) => (
                                    <label
                                        key={r.id}
                                        data-testid={`report-reason-${r.id}`}
                                        className={`flex items-center gap-3 p-3 rounded-2xl border cursor-pointer transition-colors ${reason === r.id ? "border-primary bg-primary/8" : "border-border hover:bg-muted"}`}
                                    >
                                        <input
                                            type="radio" name="reason" value={r.id}
                                            checked={reason === r.id}
                                            onChange={() => setReason(r.id)}
                                            className="accent-primary"
                                        />
                                        <span className="text-sm font-medium">{r.label}</span>
                                    </label>
                                ))}
                            </div>
                            <div>
                                <label htmlFor="report-desc" className="text-sm font-medium">Additional details (optional)</label>
                                <Textarea
                                    id="report-desc"
                                    data-testid="report-description-input"
                                    className="mt-1.5 rounded-xl min-h-24"
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    placeholder="Tell us more so we can review this faster…"
                                />
                            </div>
                            <div className="flex gap-2 pt-1">
                                <Button variant="ghost" onClick={() => onOpenChange(false)}
                                    className="flex-1 h-11 rounded-full">
                                    Cancel
                                </Button>
                                <Button
                                    onClick={submit}
                                    disabled={submitting}
                                    data-testid="report-submit-btn"
                                    className="flex-1 h-11 rounded-full bg-primary hover:bg-primary/90 font-semibold"
                                >
                                    {submitting ? "Sending…" : "Submit report"}
                                </Button>
                            </div>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
